import { cardCharges, fmtEur, virtualCards } from "@/shared/data";
import { icon } from "@/shared/icons";
import type { Route } from "@/shared/layout";

interface Ctx {
  canvas: HTMLElement;
  navigate: (r: Route) => void;
}

function cardArt(card: (typeof virtualCards)[number]): string {
  const grad =
    card.brand === "visa"
      ? "linear-gradient(135deg, var(--accent) 0%, color-mix(in srgb, var(--accent) 50%, var(--ink-primary)) 100%)"
      : "linear-gradient(135deg, var(--ink-primary) 0%, color-mix(in srgb, var(--ink-primary) 60%, var(--accent)) 100%)";
  const isFrozen = card.status === "frozen";
  const usagePct = Math.min(100, (card.spent30d / card.limit) * 100);

  return `<div class="vcard ${isFrozen ? "is-frozen" : ""}" data-card-id="${card.id}">
    <div class="vcard-art" style="background: ${grad};">
      <div class="vcard-art-mark">
        <svg width="40" height="40" viewBox="0 0 151 164" fill="white" opacity="0.85" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.2));">
          <use href="#card-lion" />
        </svg>
      </div>
      <div class="vcard-art-status">
        <span class="status-seal is-${card.status === "active" ? "success" : card.status === "frozen" ? "pending" : "failed"}"></span>
        ${card.statusLabel}
      </div>
      <div class="vcard-art-num">•••• •••• •••• <strong>${card.last4}</strong></div>
      <div class="vcard-art-foot">
        <div>
          <span class="vcard-art-foot-label">Holder</span>
          <span class="vcard-art-foot-value">${card.holder}</span>
        </div>
        <div>
          <span class="vcard-art-foot-label">Expiry</span>
          <span class="vcard-art-foot-value">${card.expiry}</span>
        </div>
        <div class="vcard-art-brand">${card.brand === "visa" ? "VISA" : "MC"}</div>
      </div>
    </div>
    <div class="vcard-meta">
      <div>
        <div class="vcard-meta-label">${card.label}</div>
        <div class="vcard-meta-cat">${card.category}</div>
      </div>
      <div class="vcard-meta-spend">
        <div style="display: flex; justify-content: space-between; font-size: 0.66rem; color: var(--ink-muted); font-variant-numeric: tabular-nums; letter-spacing: 0.04em; margin-bottom: 0.35rem;">
          <span>${card.cycleLabel}</span>
          <span><strong style="color: var(--ink-primary); font-weight: 700;">${fmtEur(card.spent30d, { compact: true })}</strong> / ${fmtEur(card.limit, { compact: true })}</span>
        </div>
        <div style="height: 4px; background: var(--surface-canvas); position: relative;">
          <div style="position: absolute; inset: 0; right: ${100 - usagePct}%; background: ${usagePct > 80 ? "var(--state-warning)" : "var(--accent)"};"></div>
        </div>
      </div>
      <div class="vcard-meta-actions">
        <button class="btn btn-ghost btn-sm">${card.status === "frozen" ? "Unfreeze" : "Freeze"}</button>
        <button class="btn btn-ghost btn-sm">Show details</button>
      </div>
    </div>
  </div>`;
}

export function renderCards(ctx: Ctx) {
  const totalLimit = virtualCards.reduce((a, c) => a + c.limit, 0);
  const totalSpent = virtualCards.reduce((a, c) => a + c.spent30d, 0);
  const active = virtualCards.filter((c) => c.status === "active").length;

  ctx.canvas.innerHTML = `
    <!-- Lion symbol for use in card art -->
    <svg width="0" height="0" style="position: absolute;" aria-hidden="true">
      <symbol id="card-lion" viewBox="0 0 151 164">
        <path d="M79.757 0.18637C86.968 -0.738904 97.5336 1.83166 103.055 6.77315C108.776 11.8937 110.111 16.6109 110.645 23.9435C113.429 25.9885 117.525 27.637 120.328 29.8479C122.584 31.4834 123.525 34.6057 125.884 36.0566C131.424 39.4603 141.615 43.6829 145.417 48.8612C143.583 55.7382 141.024 62.5521 138.31 69.1127C137.951 69.9807 138.083 72.5627 138.139 73.6044C138.686 83.7837 130.881 93.0018 121.844 96.7392C123.131 86.842 118.652 84.0692 110.236 80.1587C108.642 79.4168 104.877 80.5653 104.349 82.2545C105.428 84.6315 108.817 87.9342 110.269 90.5686C120.884 109.822 114.596 126.771 96.4457 137.866C103.352 127.125 102.413 113.636 95.8963 102.979C87.2643 88.9162 68.5253 79.0167 69.4532 60.6356C69.7906 53.9922 72.3925 48.6293 76.1472 43.2801C76.1494 43.4594 76.1559 43.6387 76.1645 43.8178C76.8047 56.7563 84.6343 64.4241 93.4912 72.3903L101.334 79.5163C106.092 75.0749 107.359 71.9736 110.394 66.5411C113.889 68.8562 116.216 69.4584 120.419 68.9101C124.537 68.3726 128.8 67.6591 132.71 66.2448C135.429 65.262 137.03 61.6394 138.053 59.1287C137.291 57.9487 136.372 57.1257 135.325 56.2104C133.009 54.1836 130.928 52.0756 128.878 49.7841C131.53 49.9461 135.989 49.5077 139.002 49.4737C133.608 44.3099 126.053 42.014 119.926 38.585C119.091 37.3759 118.401 35.2338 117.752 34.49C113.078 29.1348 103.983 25.799 98.4399 21.7739L98.1846 21.5875C97.8386 20.7831 97.4082 19.358 97.1789 18.5082C95.321 11.5976 90.7617 6.26466 84.7035 2.59385C83.3344 1.76418 81.5522 1.23925 80.1334 0.41022L79.757 0.18637ZM122.218 81.6554C124.351 83.5176 125.024 84.4844 126.641 86.8463C130.269 84.4823 132.617 80.5632 133.493 76.3596C134.27 72.641 134.103 70.3447 129.568 71.1181C126.782 71.5187 114.99 73.4082 113.571 74.729C113.483 75.0935 113.316 75.4296 113.647 75.7653C115.892 78.0456 119.619 79.4882 122.218 81.6554Z"/>
        <path d="M34.5263 95.5907C35.0997 95.9865 38.5843 100.483 39.9477 101.675C44.9325 106.027 50.1582 109.868 55.2346 114.049C55.4656 119.858 54.9608 125.975 55.4323 131.732C55.7568 135.695 57.6839 139.717 60.8776 142.209C62.6582 143.583 64.7508 144.493 66.9682 144.863C69.7734 145.36 81.0895 145.224 84.4549 145.179C87.9977 145.131 98.4162 145.453 101.708 145.051C102.104 149.998 101.812 158.364 101.782 163.561C101.228 163.92 100.127 163.831 99.4068 163.833L78.0507 163.818C64.8497 163.818 54.1084 165.092 43.7976 154.769C39.9642 150.973 37.1966 146.236 35.7725 141.035C33.9473 134.392 34.517 125.529 34.5181 118.474L34.5263 95.5907Z"/>
        <path d="M5.43665 83.6949C7.23464 83.7988 10.1856 83.695 12.0775 83.6993L26.0007 83.7447C26.1117 86.3618 25.9709 89.5628 25.9934 92.262L26.0611 118.9L26.1309 146.489C26.1448 152.171 26.2722 158.348 26.0518 163.961C25.2361 164.052 21.5982 163.872 20.3948 163.881C15.3789 163.918 10.2748 163.719 5.26319 164C5.05663 160.1 5.15245 155.511 5.14207 151.555C5.09859 143.643 5.10205 135.733 5.15244 127.824L5.0947 98.8393C5.09427 97.1069 4.87971 84.3719 5.43665 83.6949Z"/>
        <path d="M48.7383 45.0511C49.1352 45.4335 48.5685 46.4352 48.3358 47.0637C47.0618 50.5046 46.5488 55.6405 46.729 59.2683C47.5588 75.9749 62.3879 85.793 73.7103 95.9174C81.1786 102.594 87.0465 109.219 87.5721 119.8C88.0241 128.895 84.2802 135.083 78.3864 141.612C78.3972 140.23 78.2912 139.194 78.1571 137.823C76.9373 129.918 75.2762 126.961 70.9569 120.475C59.3235 103.007 31.1055 95.4156 33.1646 70.7334C34.1318 59.1402 40.3887 52.2578 48.7383 45.0511Z"/>
      </symbol>
    </svg>

    <section class="page-head">
      <span class="page-head-watermark"></span>
      <div>
        <span class="page-eyebrow">Business Account<span class="page-eyebrow-rule"></span>Virtual Cards</span>
        <h1 class="page-title">Cards for ad spend, subscriptions, vendors</h1>
      </div>
      <div class="page-head-meta" style="flex-direction: row; align-items: center; gap: 0.6rem;">
        <button class="btn btn-ghost btn-sm">Card rules</button>
        <button class="btn btn-primary">${icon("plus", 11)} Issue new card</button>
      </div>
    </section>

    <section class="summary-row">
      <div class="summary-cell">
        <span class="summary-cell-label">Active cards</span>
        <span class="summary-cell-value">${active}</span>
        <span class="summary-cell-note">${virtualCards.length - active} frozen / expired</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Spent · 30d</span>
        <span class="summary-cell-value">${fmtEur(totalSpent, { compact: true })}</span>
        <span class="summary-cell-note">${((totalSpent / totalLimit) * 100).toFixed(0)}% of total limit</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Total limit</span>
        <span class="summary-cell-value">${fmtEur(totalLimit, { compact: true })}</span>
        <span class="summary-cell-note">Across ${virtualCards.length} cards</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Top category</span>
        <span class="summary-cell-value" style="font-size: 1.4rem; letter-spacing: -0.02em;">Advertising</span>
        <span class="summary-cell-note">${fmtEur(8420, { compact: true })} this month</span>
      </div>
    </section>

    <section class="vcards-grid">
      ${virtualCards.map(cardArt).join("")}
    </section>

    <section class="panel" style="margin-top: 1.5rem;">
      <div class="panel-head">
        <div>
          <div class="panel-eyebrow">Card spend</div>
          <h2 class="panel-title">Recent charges</h2>
          <p class="panel-sub">Every charge across every active card.</p>
        </div>
        <button class="btn btn-ghost btn-sm">Export ${icon("download", 11)}</button>
      </div>
      <div class="ledger-table">
        <div class="ledger-table-header" style="grid-template-columns: 90px minmax(0, 1.4fr) 80px 1fr 130px 100px;">
          <span>Date / Time</span>
          <span>Merchant</span>
          <span>Card</span>
          <span>Category</span>
          <span style="text-align: right;">Amount</span>
          <span>Status</span>
        </div>
        ${cardCharges
          .map(
            (c) => `<div class="ledger-table-row" style="grid-template-columns: 90px minmax(0, 1.4fr) 80px 1fr 130px 100px;">
          <div class="col-date">
            <strong>${c.date}</strong>
            ${c.time}
          </div>
          <div class="col-name" style="min-width: 0;">
            <strong>${c.merchant}</strong>
          </div>
          <div class="col-method" style="font-family: ui-monospace, monospace; font-size: 0.78rem; letter-spacing: 0.04em;">••${c.cardLast4}</div>
          <div class="col-method" style="color: var(--ink-muted);">${c.category}</div>
          <div class="col-amount is-out">−${fmtEur(c.amountEur)}</div>
          <div class="col-status">
            <span class="status-seal is-${c.status}"></span> ${c.statusLabel}
          </div>
        </div>`,
          )
          .join("")}
      </div>
    </section>
  `;

  ctx.canvas.querySelectorAll<HTMLElement>("[data-route]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      const route = el.getAttribute("data-route") as Route;
      if (route) ctx.navigate(route);
    });
  });
}
